import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Modal from '../../../components/ui/Modal';
import SignaturePad from '../../document-and-form-generation-center/components/SignaturePad';
import { signatureService } from '../../../services/signatureService';
import { useAuth } from '../../../contexts/AuthContext';
import { supabase } from '../../../lib/supabase';

const SignaturePanel = ({ contract, onSignatureComplete }) => {
  const { user, userProfile } = useAuth();
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  const [signatures, setSignatures] = useState([]);
  const [timeline, setTimeline] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (contract?.id) {
      loadSignatures();
      loadTimeline();
    }
  }, [contract]);

  const loadSignatures = async () => {
    try {
      const sigs = await signatureService?.getContractSignatures(contract?.id);
      setSignatures(sigs || []);
    } catch (err) {
      console.error('Error loading signatures:', err);
    }
  };

  const loadTimeline = async () => {
    try {
      const { data, error } = await supabase?.from('contract_approvals')?.select('*')?.eq('contract_id', contract?.id)?.order('created_at', { ascending: false });

      if (error) throw error;
      setTimeline(data || []);
    } catch (err) {
      console.error('Error loading timeline:', err);
    }
  };

  const handleSign = async (signatureData) => {
    try {
      setLoading(true);
      setError('');

      await signatureService?.createSignature(contract?.id, signatureData, {
        name: userProfile?.full_name || user?.email,
        email: user?.email,
        type: 'client'
      });

      // Update signature request status
      await supabase?.from('signature_requests')?.update({ 
          status: 'signed',
          completed_at: new Date()?.toISOString()
        })?.eq('id', contract?.signatureRequestId);

      setShowSignatureModal(false);
      onSignatureComplete();
    } catch (err) {
      setError(err?.message || 'فشل في حفظ التوقيع');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString)?.toLocaleDateString('ar-SA', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getActionColor = (action) => {
    const colors = {
      approved: '#10B981',
      rejected: '#EF4444',
      requested_changes: '#F59E0B',
      commented: '#3B82F6'
    };
    return colors?.[action] || '#6B7280';
  };

  const getActionLabel = (action) => {
    const labels = {
      approved: 'موافق',
      rejected: 'مرفوض',
      requested_changes: 'طلب تعديلات',
      commented: 'تعليق'
    };
    return labels?.[action] || action;
  };

  if (!contract) {
    return (
      <div className="w-[15%] bg-gray-800 border-r border-gray-700 flex items-center justify-center">
        <p className="text-gray-500 text-sm text-center px-4">اختر عقداً</p>
      </div>
    );
  }

  const canSign = contract?.signatureStatus === 'pending';
  const isSigned = contract?.signatureStatus === 'signed';

  return (
    <>
      <div className="w-[15%] bg-gray-800 border-r border-gray-700 flex flex-col">
        {/* Signature Actions */}
        <div className="p-4 border-b border-gray-700">
          <h3 className="text-sm font-semibold text-gray-400 mb-4">أدوات التوقيع</h3>
          
          {canSign ? (
            <Button
              fullWidth
              iconName="PenTool"
              onClick={() => setShowSignatureModal(true)}
              className="mb-3"
            >
              توقيع العقد
            </Button>
          ) : isSigned ? (
            <div className="flex items-center gap-2 p-3 bg-green-500/10 border border-green-500/30 rounded-lg mb-3">
              <Icon name="CheckCircle" size={20} color="#10B981" />
              <span className="text-sm text-green-400 font-medium">تم التوقيع</span>
            </div>
          ) : (
            <div className="flex items-center gap-2 p-3 bg-gray-700/50 border border-gray-600 rounded-lg mb-3">
              <Icon name="Lock" size={20} color="#6B7280" />
              <span className="text-sm text-gray-400 font-medium">غير متاح</span>
            </div>
          )}

          <Button
            fullWidth
            variant="outline"
            iconName="MessageSquare"
            size="sm"
            className="mb-2"
          >
            إضافة تعليق
          </Button>

          <Button
            fullWidth
            variant="outline"
            iconName="Share2"
            size="sm"
          >
            مشاركة
          </Button>
        </div>

        {/* Signatures List */}
        <div className="p-4 border-b border-gray-700">
          <h3 className="text-sm font-semibold text-gray-400 mb-3">
            التوقيعات ({signatures?.length || 0})
          </h3>
          {signatures?.length === 0 ? (
            <p className="text-xs text-gray-500">لا توجد توقيعات بعد</p>
          ) : (
            <div className="space-y-2">
              {signatures?.map(sig => (
                <div key={sig?.id} className="p-2 bg-gray-900/50 rounded-lg">
                  <div className="flex items-center gap-2 mb-1">
                    <Icon name="CheckCircle" size={14} color="#10B981" />
                    <span className="text-xs text-white font-medium">{sig?.signerName}</span>
                  </div>
                  <p className="text-xs text-gray-500">{formatDate(sig?.signedAt)}</p>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Status Timeline */}
        <div className="flex-1 overflow-y-auto p-4">
          <h3 className="text-sm font-semibold text-gray-400 mb-3">الجدول الزمني</h3>
          {timeline?.length === 0 ? (
            <p className="text-xs text-gray-500">لا توجد أحداث</p>
          ) : (
            <div className="space-y-3">
              {timeline?.map((event, index) => (
                <div key={event?.id} className="relative">
                  {/* Timeline Line */}
                  {index < timeline?.length - 1 && (
                    <div className="absolute right-2 top-6 bottom-0 w-px bg-gray-700"></div>
                  )}
                  
                  {/* Event */}
                  <div className="flex gap-3">
                    <div
                      className="flex-shrink-0 w-4 h-4 rounded-full border-2 mt-1"
                      style={{
                        borderColor: getActionColor(event?.action),
                        backgroundColor: `${getActionColor(event?.action)}40`
                      }}
                    ></div>
                    <div className="flex-1">
                      <p className="text-xs text-white font-medium mb-1">
                        {getActionLabel(event?.action)}
                      </p>
                      <p className="text-xs text-gray-500 mb-1">
                        {event?.approver_name}
                      </p>
                      {event?.comments && (
                        <p className="text-xs text-gray-400 mb-1">{event?.comments}</p>
                      )}
                      <p className="text-xs text-gray-600">{formatDate(event?.created_at)}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Contract Info */}
        <div className="p-4 border-t border-gray-700 bg-gray-900/50">
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-xs text-gray-500">تاريخ الطلب</span>
              <span className="text-xs text-white">{formatDate(contract?.requestedAt)}</span>
            </div>
            {contract?.dueDate && (
              <div className="flex items-center justify-between">
                <span className="text-xs text-gray-500">تاريخ الاستحقاق</span>
                <span className="text-xs text-amber-500">{formatDate(contract?.dueDate)}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Signature Modal */}
      <Modal
        isOpen={showSignatureModal}
        onClose={() => setShowSignatureModal(false)}
        title="التوقيع الإلكتروني"
        icon="PenTool"
        iconColor="#DAA520"
        size="lg"
      >
        <div className="space-y-6">
          <div className="p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg">
            <div className="flex items-start gap-3">
              <Icon name="Info" size={20} color="#F59E0B" className="flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm text-amber-400 font-medium mb-1">
                  تنبيه قانوني
                </p>
                <p className="text-xs text-amber-300">
                  بالتوقيع على هذا العقد، فإنك توافق على جميع الشروط والأحكام المذكورة. التوقيع الإلكتروني له نفس القوة القانونية للتوقيع اليدوي.
                </p>
              </div>
            </div>
          </div>

          <SignaturePad
            onSave={handleSign}
            onCancel={() => setShowSignatureModal(false)}
            loading={loading}
            signerName={userProfile?.full_name || user?.email || ''}
          />

          {error && (
            <div className="flex items-start gap-2 p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
              <Icon name="AlertCircle" size={20} color="#EF4444" className="flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-400">{error}</p>
            </div>
          )}

          <div className="flex items-center gap-2 text-xs text-gray-500">
            <Icon name="Shield" size={14} color="#10B981" />
            <span>التوقيع محمي بتشفير 256-bit ويتم تسجيله في سجل التدقيق</span>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default SignaturePanel;